import type { QuoteLineMO, QuoteLinePartMO } from "~/OrdersQuotes/common/components/v2/models";
import { fakeDescriptorFromProduct, getTotalPrice } from "~/feature/ProductDescriptor/v1/productDescriptorUtils";
import { getPackagingLabelAtLineItem } from "~/Cart/cartUtils";
import { ProductDescriptorContext } from "~/feature/Product/ProductContext";
import classes from "~/OrdersQuotes/common/components/v2/QuoteTable/ProductHeader.module.less";
import { Stacker } from "~/common/components/Stacker/Stacker";
import { Product } from "~/feature/Product";
import ProductMMD from "../../../../../feature/Product/elements/ProductMMD";
import { AliasesParameter, IncludedSurcharge, SchaefflerIdParameter } from "~/common/components/Product/ProductParams";
import Parameter, { ReverseParam } from "~/common/components/Parameter";
import { DateFormatType, formatNullableDate } from "~/utils/DateFormatter";
import { WeightFormat } from "~/common/components/formatting/WeightFormat";
import { Price } from "~/common/components/Price/v3/Price";
import Chip from "~/common/components/Chip/Chip";
import { Trans, useTranslation } from "react-i18next";
import type { RenderCboxFn } from "~/OrdersQuotes/common/components/v2/QuoteTable/types";
import { makeCountableParam } from "~/OrdersQuotes/common/components/v2/QuoteTable/useLineNoOffsetCalculation";
import { QuoteLineMessages } from "~/OrdersQuotes/common/components/v2/QuoteTable/QuoteLineMessages";

import {
  getPriceReferenceLabelKey,
  isPriceReferenceParamVisible,
} from "~/Cart/components/CartPage/LineItemStaticParams";
import { getEDD } from "~/OrdersQuotes/common/components/v2/QuoteTable/utils";
import { translateWarehouseName } from "~/utils/language/translationUtils";

export const ProductHeader = ({
  quoteLine,
  includeQuoteLine,
  renderCheckbox,
  hidden,
  alternative,
}: {
  quoteLine: QuoteLineMO;
  includeQuoteLine: boolean;
  renderCheckbox?: RenderCboxFn;
  hidden?: boolean;
  alternative?: boolean;
}) => {
  const productDescriptor = fakeDescriptorFromProduct(quoteLine.product);
  const { t } = useTranslation();

  const packagingLabel = getPackagingLabelAtLineItem({
    t,
    packSize: quoteLine.packSize,
    packCode: quoteLine.packCode,
    quantity: quoteLine.parts.reduce((acc, part) => acc + part.confirmedQuantity, 0),
    productDescriptor,
  });
  const quotePart = includeQuoteLine && quoteLine.parts[0];
  const confirmedQty = (quotePart: QuoteLinePartMO) =>
    quotePart.confirmedQuantity === 0 ? "-" : (quotePart as QuoteLinePartMO).confirmedQuantity;
  // SCHAEFF-31697, SCHAEFF-31776: Use requested quantity to calculate weights and prices when confirmed quantity is not available
  const quantity = (quotePart: QuoteLinePartMO) =>
    quotePart.confirmedQuantity ? quotePart.confirmedQuantity : quotePart.requestedQuantity;
  const totalPrice = (quotePart: QuoteLinePartMO) => getTotalPrice(quotePart.price, quantity(quotePart));
  return (
    <ProductDescriptorContext value={productDescriptor}>
      <table className={classes.productHeader} {...makeCountableParam(quoteLine.lineNo)} hidden={hidden}>
        <tbody>
          <tr>
            <td>
              <Stacker gap={8}>
                <Stacker gap={8} direction={"row"}>
                  <Product.PreferredIndicator className={"m-0"} />
                  <Product.Designation className={classes.designation} />
                </Stacker>
                {productDescriptor.base.marketDesignation && (
                  <ProductMMD marketDesignation={productDescriptor.base.marketDesignation} />
                )}
                {alternative && (
                  <Chip className={classes.chip} type={"blue"}>
                    {t("page.pdp.phaseinout.alternative.tag")}
                  </Chip>
                )}
                <Product.ShortDescription clickable={false} />
                <SchaefflerIdParameter />
              </Stacker>
            </td>
            <td>
              {quotePart && <ReverseParam label={confirmedQty(quotePart)}>{quotePart.requestedQuantity}</ReverseParam>}
            </td>
            <td>
              <Stacker gap={16}>
                <ReverseParam label={packagingLabel}>{translateWarehouseName(t, quoteLine.warehouseName)}</ReverseParam>
                <AliasesParameter mode={"all"} packCode={quoteLine.packCode} maxAliasesVisible={99} />
              </Stacker>
            </td>
            <td>
              {quotePart && (
                <ReverseParam label={getEDD(quotePart)}>
                  {formatNullableDate(quotePart.requestedDeliveryDate, DateFormatType.DATE)}
                </ReverseParam>
              )}
            </td>
            <td>
              <ReverseParam label={<WeightFormat weight={quoteLine.weight} />}>
                {t("common.product.perPiece", { count: 1 })}
              </ReverseParam>
            </td>
            <td>
              <Stacker gap={16}>
                {quotePart && (
                  <ReverseParam
                    label={
                      <Price price={quotePart.price} zeroText={t("product.priceOnRequest")} surchargeEnabled={true} />
                    }
                  >
                    <Price.NetPriceUnit price={quotePart.price} variant={"short"} surchargeEnabled={true} />
                  </ReverseParam>
                )}
                {renderPriceReferenceParam(quoteLine)}
              </Stacker>
            </td>
            <td>
              {quotePart && quotePart.price && (
                <Stacker gap={16}>
                  <ReverseParam label={<Price as={"b"} price={totalPrice(quotePart)} surchargeEnabled={true} />}>
                    <Price.TotalPriceUnit price={totalPrice(quotePart)} surchargeEnabled={false} variant={"short"} />
                  </ReverseParam>
                  <IncludedSurcharge calculateFrom={totalPrice(quotePart)} />
                </Stacker>
              )}
            </td>
            <td>{quotePart && <Chip>{t(`codes.quoteLineItemStatus.${quoteLine.status}`)}</Chip>}</td>
            <td>{renderCheckbox?.(quoteLine)}</td>
          </tr>
          <tr>
            <QuoteLineMessages entry={quoteLine} className={classes.productHeaderMessages} />
          </tr>
        </tbody>
      </table>
    </ProductDescriptorContext>
  );
};

function renderPriceReferenceParam(quoteLine: QuoteLineMO) {
  if (!isPriceReferenceParamVisible() || !quoteLine.priceReference) {
    return null;
  }
  return (
    <Parameter
      label={<Trans i18nKey={getPriceReferenceLabelKey()!} />}
      value={[quoteLine.priceReference, quoteLine.priceReferenceDescription].filter(Boolean).join("-")}
    />
  );
}
